/**
 * pullRequestPlan — the data and arithmetic behind the plan contract's Usage
 * page (`billingContract === "pr_plan"`): a monthly bucket of merged pull
 * requests YAGNI authored.
 *
 * The counts come from `GET /api/yagni-code-usage/pull-requests?month=`. The
 * bucket and the plan's name do not have a backend field yet, so they come
 * from the tier ladder below, Growth by default. The ladder is the
 * placeholder from the Oct 9 call, not a price list; the page says so.
 */
import { useEffect, useState } from "react";
import type { PullRequestPlanResponse } from "@yagni/shared";

import { UsageAccessError } from "./usageReport.js";

export interface PlanTier {
  name: string;
  /** Whole dollars a month. */
  priceUsd: number;
  /** Merged pull requests a month. */
  bucket: number;
}

/** The ladder from the Oct 9 call. Placeholder numbers. */
export const PLAN_TIERS: readonly PlanTier[] = [
  { name: "Starter", priceUsd: 1_000, bucket: 15 },
  { name: "Growth", priceUsd: 2_500, bucket: 50 },
  { name: "Scale", priceUsd: 5_000, bucket: 120 },
  { name: "Fleet", priceUsd: 10_000, bucket: 275 },
];

/** No workspace carries its tier yet, so every plan workspace reads as Growth. */
export const DEFAULT_PLAN_TIER = "Growth";

export function planTier(name: string = DEFAULT_PLAN_TIER): PlanTier {
  return PLAN_TIERS.find((tier) => tier.name === name) ?? PLAN_TIERS[1]!;
}

/** The next rung up, or null at the top of the ladder. */
export function nextTier(tier: PlanTier): PlanTier | null {
  const index = PLAN_TIERS.findIndex((candidate) => candidate.name === tier.name);
  return index >= 0 && index < PLAN_TIERS.length - 1 ? PLAN_TIERS[index + 1]! : null;
}

/** Dollars a pull request at a tier, rounded to the whole dollar. */
export function perPullRequestUsd(tier: PlanTier): number {
  return Math.round(tier.priceUsd / tier.bucket);
}

/** "2026-10" for a date, in UTC. */
export function monthKey(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** The month `offset` months before `now`'s ("2026-09" for offset 1 in October). */
export function monthKeyBefore(now: Date, offset: number): string {
  return monthKey(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset, 1)));
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** "October" for "2026-10". */
export function monthName(key: string): string {
  const month = Number(key.slice(5, 7));
  return MONTH_NAMES[month - 1] ?? key;
}

export function daysInMonth(key: string): number {
  const year = Number(key.slice(0, 4));
  const month = Number(key.slice(5, 7));
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** "Nov 1": the first day of the month after `key`, when the bucket resets. */
export function renewsLabel(key: string): string {
  const year = Number(key.slice(0, 4));
  const month = Number(key.slice(5, 7));
  const next = new Date(Date.UTC(year, month, 1));
  return `${MONTH_NAMES[next.getUTCMonth()]!.slice(0, 3)} ${next.getUTCDate()}`;
}

/**
 * Where the month lands at this pace: merged ÷ days elapsed × days in the
 * month. A month that is over has landed where it is; a month that has not
 * started has no pace.
 */
export function planPace(merged: number, key: string, now: Date): number {
  const current = monthKey(now);
  if (key < current) return merged;
  if (key > current) return 0;
  const elapsed = Math.max(1, now.getUTCDate());
  return Math.round((merged / elapsed) * daysInMonth(key));
}

/** "1h 04m", "52m", "2d 3h"; null reads as a dash. */
export function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return "-";
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/** Fix rounds per merged pull request, one decimal. */
export function formatRounds(value: number | null): string {
  return value === null ? "-" : value.toFixed(1);
}

/** "today 10:48", "yesterday", "Oct 7": when a pull request merged. */
export function mergedWhen(iso: string, now: Date): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const day = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = Math.round((day(now) - day(at)) / 86_400_000);
  if (diff === 0) {
    return `today ${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
  }
  if (diff === 1) return "yesterday";
  return at.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** The merged list as a spreadsheet, for the Download CSV button. */
export function pullRequestCsv(plan: PullRequestPlanResponse): string {
  const cell = (value: string | null) => {
    const text = value ?? "";
    return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
  };
  const head = ["Pull request", "Title", "Team", "Repository", "Merged by", "Merged at", "URL"];
  const lines = plan.rows.map((row) =>
    [row.pr, row.title, row.teamName, row.repo, row.mergedBy, row.mergedAt, row.url].map(cell).join(","),
  );
  return [head.join(","), ...lines].join("\n");
}

export async function fetchPullRequestPlan(month: string): Promise<PullRequestPlanResponse> {
  const res = await fetch(`/api/yagni-code-usage/pull-requests?month=${encodeURIComponent(month)}`, {
    credentials: "include",
  });
  if (res.status === 403) throw new UsageAccessError();
  if (!res.ok) throw new Error(`Failed to load pull requests (${res.status})`);
  return (await res.json()) as PullRequestPlanResponse;
}

export interface PullRequestPlanState {
  plan: PullRequestPlanResponse | null;
  error: string | null;
  loading: boolean;
}

export function usePullRequestPlan(month: string): PullRequestPlanState {
  const [state, setState] = useState<PullRequestPlanState>({ plan: null, error: null, loading: true });
  useEffect(() => {
    let cancelled = false;
    setState({ plan: null, error: null, loading: true });
    fetchPullRequestPlan(month)
      .then((plan) => {
        if (!cancelled) setState({ plan, error: null, loading: false });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof UsageAccessError
            ? "Usage is visible to workspace admins."
            : err instanceof Error
              ? err.message
              : "Failed to load pull requests";
        setState({ plan: null, error: message, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [month]);
  return state;
}
